import React, { useMemo, useState, useEffect } from "react";
import { Calendar, Euro, MapPin } from "lucide-react";
import CategoryBadge from "../CategoryBadge";
import { formatDateShort } from "@/utils/dateUtils";
import { formatNumberMoney } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

import { Measure } from "@/types";

interface DocumentedMeasuresListProps {
  documentedMeasurements: (Measure & {
    batchTitle?: string;
  })[];
}

const DocumentedMeasuresList = ({
  documentedMeasurements,
}: DocumentedMeasuresListProps) => {
  const { t, language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Sort measures by date for timeline display
  const sortedMeasures = useMemo(() => {
    if (documentedMeasurements.length === 0) {
      return [];
    }

    // Deduplicate measures by id
    const uniqueMeasures = Array.from(
      new Map(
        documentedMeasurements.map((measure) => [measure.id, measure])
      ).values()
    );

    // Sort unique measures by date
    return [...uniqueMeasures].sort((a, b) => {
      return (
        new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime()
      );
    });
  }, [documentedMeasurements]);

  if (documentedMeasurements.length === 0) {
    return (
      <p className="text-sm text-center text-muted-foreground py-4">
        {t("documentedMeasures.noMeasures")}
      </p>
    );
  }

  return (
    <div className="relative mt-4 pl-5 border-l-2 border-silvaconsult-primary-200">
      {sortedMeasures.map((measure) => (
        <div key={measure.id} className="mb-3 relative">
          {/* Timeline dot */}
          <div className="absolute -left-7 mt-1 w-2.5 h-2.5 rounded-full bg-primary border-2 border-white"></div>

          {/* Date */}
          <div className="mb-1">
            <div className="flex items-center gap-1 text-xs font-medium">
              <Calendar className="h-3 w-3 text-silvaconsult-primary-400" />
              <time className="text-silvaconsult-primary-700">
                {formatDateShort(
                  typeof measure.invoiceDate === "string"
                    ? measure.invoiceDate
                    : new Date(measure.invoiceDate).toISOString() || ""
                )}
              </time>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white p-2 border border-silvaconsult-primary-100 rounded-lg shadow-sm hover:border-silvaconsult-primary-200 transition-colors">
            <div className="flex flex-col gap-1.5">
              {/* Title row with category badge */}
              {isMobile ? (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <CategoryBadge category={measure.category} size="xs" />
                    <div className="text-sm font-medium text-silvaconsult-primary-900">
                      {measure.description ||
                        t("documentedMeasures.unnamedMeasure")}
                    </div>
                  </div>
                  {measure.invoiceNumber && (
                    <div className="text-xs text-silvaconsult-primary-600">
                      {t("documentedMeasures.invoiceNo")}:{" "}
                      {measure.invoiceNumber}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <CategoryBadge category={measure.category} size="xs" />
                    <div className="text-sm font-medium text-silvaconsult-primary-900">
                      {measure.description ||
                        t("documentedMeasures.unnamedMeasure")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {measure.invoiceNumber && (
                      <div className="flex items-center gap-1 text-xs text-silvaconsult-primary-600 whitespace-nowrap">
                        <span>
                          {t("documentedMeasures.invoiceNo")}:{" "}
                          {measure.invoiceNumber}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location and financial info row */}
              <div
                className={
                  isMobile
                    ? "flex flex-col gap-1 mt-1"
                    : "flex flex-wrap items-start justify-between"
                }
              >
                {/* Location */}
                {measure.forestLocation ? (
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin className="h-2.5 w-2.5 text-silvaconsult-primary-500 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-silvaconsult-primary-800 truncate max-w-[16rem]">
                      {measure.forestLocation}
                    </div>
                  </div>
                ) : (
                  <div className="flex-grow"></div>
                )}

                {/* Financial details */}
                <div
                  className={
                    isMobile ? "flex flex-row gap-2" : "flex flex-col items-end"
                  }
                >
                  <div className="flex items-center gap-1">
                    <Euro className="h-2.5 w-2.5 text-gray-500" />
                    <span className="text-xs font-medium text-right">
                      {formatNumberMoney(measure.totalNet || 0, language)}
                    </span>
                  </div>

                  {measure.totalGross &&
                    measure.totalGross !== measure.totalNet && (
                      <span className="text-[9px] text-gray-500">
                        {t("documentedMeasures.gross")}:{" "}
                        {formatNumberMoney(measure.totalGross, language)}
                      </span>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentedMeasuresList;
